import { type FC, lazy, Suspense } from 'react';
import { cn } from '../lib/utils';

const LazyReactMarkdown = lazy(async () => {
  const [{ default: ReactMarkdown }, { default: remarkGfm }, { default: remarkBreaks }] =
    await Promise.all([import('react-markdown'), import('remark-gfm'), import('remark-breaks')]);

  const WrappedMarkdown: FC<{ children: string }> = ({ children }) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={{
        a: ({ href, children }) => (
          <a href={href} rel="noopener noreferrer" target="_blank">
            {children}
          </a>
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  );

  return { default: WrappedMarkdown };
});

const mdBody = cn(
  'prose prose-sm overflow-x-auto',
  'prose-h1:heading-lg prose-h2:heading-md prose-h3:heading-sm prose-h4:heading-xs',
  '[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1',
  '[&_th]:border [&_th]:border-border [&_th]:bg-muted/50 [&_th]:px-2 [&_th]:py-1 [&_th]:font-semibold',
);

export type MarkdownProps = {
  children: string | undefined;
  className?: string;
};

/** GFM + soft line breaks; default Solink text styles. */
export const Markdown: FC<MarkdownProps> = ({ children = '', className }) => {
  return (
    <div className={cn('min-w-0', mdBody, className)}>
      <Suspense>
        <LazyReactMarkdown>{children}</LazyReactMarkdown>
      </Suspense>
    </div>
  );
};
